/**
 * @file date-time-pickers/shared/types.ts
 *
 * Shared TypeScript base types for Date Pickers and Time Pickers system.
 * Designed for reuse across both picker systems.
 *
 * @see https://m3.material.io/components/date-pickers/overview
 */

// ─── Display Mode ─────────────────────────────────────────────────────────────

/**
 * Picker display mode.
 * - `picker` — calendar/dial UI (default)
 * - `input`  — text input mode for manual entry
 *
 * Equivalent to `DisplayMode` enum in Android Compose Material3.
 */
export type DisplayMode = "picker" | "input";

// ─── Week Configuration ───────────────────────────────────────────────────────

/**
 * First day of the week.
 * - `0` = Sunday (US standard)
 * - `1` = Monday (ISO 8601, default for this library)
 */
export type WeekStartsOn = 0 | 1;

// ─── Locale Configuration ─────────────────────────────────────────────────────

/**
 * Locale configuration for Date Picker.
 * Uses native `Intl.DateTimeFormat` API — zero bundle overhead.
 *
 * @example
 * ```tsx
 * const state = useDatePickerState({
 *   locale: { locale: 'en-US', weekStartsOn: 1 }
 * });
 * ```
 */
export interface DateLocale {
	/**
	 * BCP 47 language tag.
	 * @example 'en-US', 'fr-FR', 'ja-JP'
	 * @default navigator.language (runtime)
	 */
	locale?: string;

	/**
	 * First day of the week.
	 * @default 1 (Monday)
	 */
	weekStartsOn?: WeekStartsOn;
}

/** Required version — all fields resolved */
export type ResolvedDateLocale = Required<DateLocale>;

// ─── Selectable Dates ─────────────────────────────────────────────────────────

/**
 * Interface for filtering selectable dates and years.
 * Equivalent to `SelectableDates` interface in Android Compose Material3.

 *
 * @example
 * ```tsx
 * // Disable weekends
 * const selectableDates: SelectableDates = {
 *   isSelectableDate(utcMs) {
 *     const day = new Date(utcMs).getUTCDay();
 *     return day !== 0 && day !== 6;
 *   },
 *   isSelectableYear(year) {
 *     return year >= 2020;
 *   }
 * };
 * ```
 */
export interface SelectableDates {
	/**
	 * Checks if a date is selectable.
	 * @param utcTimeMs - UTC milliseconds (normalized to UTC midnight)
	 * @returns `true` if date is selectable
	 */
	isSelectableDate(utcTimeMs: number): boolean;

	/**
	 * Checks if a year is selectable.
	 * Unselectable years are disabled in year grid view.
	 * @param year - Full year (e.g., 2025)
	 * @returns `true` if year is selectable
	 */
	isSelectableYear(year: number): boolean;
}

/** Default SelectableDates — allows all dates and years */
export const ALL_DATES_SELECTABLE: SelectableDates = {
	isSelectableDate: () => true,
	isSelectableYear: () => true,
};

// ─── Date Range ───────────────────────────────────────────────────────────────

/**
 * Date range with start and end.
 * Both can be null during selection flow.
 */
export interface DateRange {
	/** Start date UTC milliseconds (UTC midnight). Null if unselected. */
	start: number | null;
	/** End date UTC milliseconds (UTC midnight). Null if unselected. */
	end: number | null;
}

// ─── Calendar Day / Week ──────────────────────────────────────────────────────

/** Represents a day cell in calendar grid */
export interface CalendarDay {
	/** UTC milliseconds (UTC midnight) */
	utcMs: number;
	/** Day of month (1–31) */
	day: number;
	/** Month index (0–11) — 0 = January */
	month: number;
	/** Full year */
	year: number;
	/** `true` if day belongs to currently displayed month */
	isCurrentMonth: boolean;
	/** `true` if day is today */
	isToday: boolean;
}

/** A single week — array of 7 days (or null for padding) */
export type CalendarWeek = Array<CalendarDay | null>;
